(() => {
  // ---- Utilities -----------------------------------------------------------
  const COMPONENT_INSTANCES = new WeakMap(); // .bundle-component => QuickAddBundleVariantSelector
  const BUTTON_CONTROLLERS = new WeakMap();  // .bundle-atc        => AbortController
  let GLOBAL_REMOVE_BOUND = false;           // guard for remove-bundle delegation

  const safeParseJSON = (str) => {
    if (!str) return null;
    try { return JSON.parse(str); } catch { return null; }
  };

  const qs = (root, sel) => (root ? root.querySelector(sel) : null);
  const qsa = (root, sel) => (root ? root.querySelectorAll(sel) : []);

  // ---- Component -----------------------------------------------------------
  class QuickAddBundleVariantSelector {
    constructor(component) {
      this.component = component;
      this.controller = new AbortController();

      if (!this.component) {
        console.warn('[QuickAddBundle] component is null');
        return;
      }

      // Cache product data once
      const raw = this.component.dataset.productJson;
      this.productData = safeParseJSON(raw);

      if (!this.productData || !Array.isArray(this.productData.variants)) {
        console.warn('[QuickAddBundle] invalid product JSON:', raw);
        return;
      }

      this.image = qs(this.component, '.bundle-component-image img');

      // Initial compute
      this.setAvailableOptions(true);
      this.updateVariant(true);
    }

    getSelectedOptions() {
      const checked = qsa(this.component, '.bundle-variant-radio:checked');
      return Array.from(checked).map((el) => el.value);
    }

    updateVariant(silent = false) {
      if (!this.component || !this.productData) return;

      const selectedOptions = this.getSelectedOptions();
      const matchingVariant = this.productData.variants.find((variant) =>
        variant.options.every((opt, idx) => opt === selectedOptions[idx])
      );

      if (matchingVariant) {
        this.component.dataset.variantId = matchingVariant.id;
      } else {
        delete this.component.dataset.variantId;
      }

      // If a color is selected and has a featured image, swap the thumb
      const colorFieldset = qs(this.component, 'fieldset.bundle-colors');
      if (colorFieldset) {
        const selectedColorRadio = qs(colorFieldset, '.bundle-variant-radio:checked');
        const featured = selectedColorRadio && selectedColorRadio.dataset.featuredImage;
        if (featured && this.image) {
          // Defer to next frame to avoid layout thrash if many updates fire
          requestAnimationFrame(() => { this.image.src = featured; });
        }
      }

      this.updateDesignatedColorSpans();

      if (!silent) {
        // Bubble a scoped event from the closest modal/bundle wrapper instead of document
        const scope = this.getScopeRoot();
        scope?.dispatchEvent(new CustomEvent('bundle:variant-change', { bubbles: true }));
      }
    }

    updateDesignatedColorSpans() {
      const groups = qsa(this.component, 'fieldset');
      groups.forEach((group) => {
        if (!group.classList.contains('bundle-colors')) return;
        const selected = qs(group, '.bundle-variant-radio:checked');
        if (!selected) return;
        const optionLabel = group.previousElementSibling;
        if (!optionLabel) return;
        const designated = optionLabel.querySelector('.designated-color');
        if (designated) designated.textContent = selected.value;
      });
    }

    setAvailableOptions(initial = false) {
      if (!this.component || !this.productData) return;

      const optionGroups = Array.from(qsa(this.component, 'fieldset'));

      optionGroups.forEach((group, optionIndex) => {
        const radios = qsa(group, '.bundle-variant-radio');

        if (optionIndex === 0) {
          let foundSelected = false;
          radios.forEach((radio) => {
            const isAvailable = this.productData.variants.some(
              (v) => v.available && v.options[0] === radio.value
            );
            radio.disabled = !isAvailable;
            const label = this.component.querySelector(`label[for="${radio.id}"]`);
            label?.classList.toggle('unavailable', !isAvailable);
            if (isAvailable && radio.checked) foundSelected = true;
          });
          // Auto-select first available if none selected
          if (!foundSelected) {
            for (const r of radios) {
              if (!r.disabled) { r.checked = true; break; }
            }
          }
        } else {
          const selectedOptions = this.getSelectedOptions();
          const previousOptions = selectedOptions.slice(0, optionIndex);

          radios.forEach((radio) => {
            const isAvailable = this.productData.variants.some(
              (v) => v.available &&
                previousOptions.every((opt, idx) => v.options[idx] === opt) &&
                v.options[optionIndex] === radio.value
            );
            radio.disabled = !isAvailable;
            const label = this.component.querySelector(`label[for="${radio.id}"]`);
            label?.classList.toggle('unavailable', !isAvailable);
          });
        }
      });
    }

    getScopeRoot() {
      // Prefer the closest modal or the bundle wrapper in this component’s ancestry
      return this.component.closest('.quick-add-modal, [data-bundle-scope], .bundle-selector') || document;
    }

    cleanup() {
      try {
        this.controller.abort();
      } catch (_) {}
      this.component = null;
      this.image = null;
      this.productData = null;
    }
  }

  // ---- Initialization per modal -------------------------------------------
  function initializeQuickAddBundle(modalContext = null) {
    const scope = modalContext || document;
    console.log('[QuickAddBundle] init', modalContext ? '(scoped to modal)' : '(global)');

    // Build / refresh component instances for current scope
    const components = qsa(scope, '.bundle-component');

    components.forEach((component) => {
      // Cleanup any stale instance tied to this DOM node
      const existing = COMPONENT_INSTANCES.get(component);
      if (existing) existing.cleanup();

      const instance = new QuickAddBundleVariantSelector(component);
      COMPONENT_INSTANCES.set(component, instance);
    });

    // Event delegation for all radios inside this scope
    bindDelegatedVariantChanges(scope);

    // ATC button wiring (scoped)
    const atcButton = qs(scope, '.bundle-atc');
    if (!atcButton) {
      console.warn('[QuickAddBundle] .bundle-atc not found in scope');
      return;
    }

    // Reset old listeners via AbortController instead of cloneNode
    const priorController = BUTTON_CONTROLLERS.get(atcButton);
    if (priorController) priorController.abort();

    const atcController = new AbortController();
    BUTTON_CONTROLLERS.set(atcButton, atcController);

    // State sync when any component changes
    const onBundleChange = () => {
      const currentComponents = qsa(scope, '.bundle-component');
      const selectedVariantIds = Array.from(currentComponents)
        .map((el) => el.dataset.variantId)
        .filter(Boolean);
      atcButton.setAttribute('data-variants-selected', selectedVariantIds.join(','));

      const allSelected = selectedVariantIds.length === currentComponents.length && currentComponents.length > 0;
      atcButton.disabled = !allSelected;

      const labelSpan = qs(atcButton, 'span');
      if (labelSpan) labelSpan.textContent = allSelected ? 'Add Bundle to Cart' : 'Select Bundle Items';
    };

    // Listen for the scoped event (bubbles)
    scope.addEventListener('bundle:variant-change', onBundleChange, { signal: atcController.signal });

    // Trigger initial state check
    onBundleChange();

    // Click handler: Add all selected bundle items
    atcButton.addEventListener('click', async (e) => {
      e.preventDefault();

      const bundleWrapper =
        qs(scope, '.bundle-selector') || qs(document, '.bundle-selector');
      if (!bundleWrapper) {
        console.error('[QuickAddBundle] .bundle-selector not found');
        return;
      }

      const bundleName = bundleWrapper.dataset.bundleName || 'Bundle';
      const bundleUrl = bundleWrapper.dataset.bundleUrl || window.location.pathname;
      const bundleDiscount = parseInt(bundleWrapper.dataset.bundleDiscount || '0', 10);
      const bundleId = parseInt(bundleWrapper.dataset.bundleId || '0', 10);
      const bundleEncryption = bundleWrapper.dataset.bundleEncryption || '';
      const bundleSeparator = Math.floor(Date.now() / 1000);

      const currentComponents = qsa(scope, '.bundle-component');
      const items = [];
      currentComponents.forEach((component) => {
        const variantId = parseInt(component.dataset.variantId || '0', 10);
        if (!variantId) return;
        items.push({
          id: variantId,
          quantity: 1,
          properties: {
            'Included In': bundleName,
            _bundle_item: true,
            _bundle_id: bundleId,
            _bundle_discount: bundleDiscount,
            _bundle_name: bundleName,
            _bundle_url: bundleUrl,
            // keep old typo key for backward-compat + correct key:
            _bundle_enctryption: bundleEncryption,
            _bundle_encryption: bundleEncryption,
            _bundle_separator: bundleSeparator
          }
        });
      });

      if (items.length !== currentComponents.length) {
        alert('Please select all bundle items.');
        return;
      }

      const formData = new FormData();
      items.forEach((item, idx) => {
        formData.append(`items[${idx}][id]`, item.id);
        formData.append(`items[${idx}][quantity]`, item.quantity);
        Object.entries(item.properties).forEach(([k, v]) => {
          formData.append(`items[${idx}][properties][${k}]`, v);
        });
      });

      const config = fetchConfig('javascript');
      config.headers['X-Requested-With'] = 'XMLHttpRequest';
      delete config.headers['Content-Type'];

      const cartDrawer = document.querySelector('cart-drawer');
      if (cartDrawer) {
        formData.append('sections', cartDrawer.getSectionsToRender().map((s) => s.id));
        formData.append('sections_url', window.location.pathname);
        cartDrawer.setActiveElement(document.activeElement);
      }

      config.body = formData;

      const startMarker = CartPerformance.createStartingMarker('add:wait-for-subscribers');

      try {
        const res = await fetch(`${routes.cart_add_url}`, config);
        const response = await res.json();

        if (response.status) {
          publish(PUB_SUB_EVENTS.cartError, {
            source: 'bundle-atc',
            errors: response.errors || response.description,
            message: response.message
          });
          alert(response.message || 'There was a problem adding your bundle to the cart.');
          return;
        }

        await publish(PUB_SUB_EVENTS.cartUpdate, {
          source: 'bundle-atc',
          cartData: response
        });
        CartPerformance.measureFromMarker('add:wait-for-subscribers', startMarker);

        if (cartDrawer) {
          CartPerformance.measure('add:paint-updated-sections', () => {
            if (response.items?.length > 0) {
              cartDrawer.classList.remove('is-empty');
              const cartDrawerItems = cartDrawer.querySelector('cart-drawer-items');
              cartDrawerItems?.classList.remove('is-empty');
            }
            cartDrawer.renderContents(response);
          });
        } else {
          window.location = window.routes.cart_url;
        }
      } catch (err) {
        console.error('Error adding bundle:', err);
        alert('There was a problem adding your bundle to the cart.');
      }
    }, { signal: atcController.signal });

  }

  // Delegated change handling per scope. We bind once per scope.
  const SCOPE_CONTROLLERS = new WeakMap();
  function bindDelegatedVariantChanges(scope) {
    let controller = SCOPE_CONTROLLERS.get(scope);
    if (controller) controller.abort(); // refresh binding on re-init
    controller = new AbortController();
    SCOPE_CONTROLLERS.set(scope, controller);

    scope.addEventListener('change', (e) => {
      const radio = e.target.closest('.bundle-variant-radio');
      if (!radio || !scope.contains(radio)) return;

      const component = radio.closest('.bundle-component');
      if (!component) return;

      const instance = COMPONENT_INSTANCES.get(component);
      if (!instance) return;

      // First recompute availability (so later options reflect the first pick), then update variant/image/labels
      instance.setAvailableOptions();
      instance.updateVariant();
    }, { signal: controller.signal });
  }

  // Expose globally
  window.initializeQuickAddBundle = initializeQuickAddBundle;
})();
